import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowUpRight } from 'lucide-react';
import Header from './Header';
import Footer from './Footer';
import SEO from './SEO';

/**
 * Shared layout for the curated directory sections (Jobs, Community,
 * Useful Information). Groups of links, each group a card.
 */
const SectionDirectory = ({ kicker, title, intro, seo, groups, children, footnote }) => (
  <div className="min-h-screen bg-slate-50">
    <SEO title={seo.title} description={seo.description} url={seo.url} />
    <Header />

    <section className="relative overflow-hidden bg-slate-900">
      <div
        className="pointer-events-none absolute inset-0 opacity-[0.06]"
        style={{ backgroundImage: 'linear-gradient(rgba(255,255,255,.7) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.7) 1px, transparent 1px)', backgroundSize: '46px 46px' }}
      />
      <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-28 pb-12">
        <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-sky-300/90">{kicker}</p>
        <h1 className="mt-3 text-4xl sm:text-5xl font-bold tracking-tight text-white">{title}</h1>
        <p className="mt-4 max-w-2xl text-base text-slate-300 leading-relaxed">{intro}</p>
      </div>
    </section>

    <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      {children}

      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
        {groups.map((group) => (
          <section
            key={group.name}
            className="rounded-2xl border border-slate-200 bg-white p-6"
            data-testid={`group-${group.name.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
          >
            <div className="flex items-center gap-2.5">
              {group.icon ? <span className="text-sky-600">{group.icon}</span> : null}
              <h2 className="text-base md:text-lg font-bold tracking-tight text-slate-900">{group.name}</h2>
            </div>
            {group.blurb ? <p className="mt-2 text-sm text-slate-500 leading-relaxed">{group.blurb}</p> : null}
            <ul className="mt-4 space-y-1">
              {group.links.map((link) => {
                const testId = `link-${link.name.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`;
                const inner = (
                  <>
                    <span className="font-semibold text-slate-800 group-hover/link:text-sky-700">{link.name}</span>
                    {link.note ? <span className="block text-xs text-slate-500 mt-0.5">{link.note}</span> : null}
                  </>
                );
                return (
                  <li key={link.name}>
                    {link.internal ? (
                      <Link
                        to={link.url}
                        className="group/link block rounded-lg px-2 py-2 -mx-2 text-sm transition-colors duration-200 hover:bg-sky-50/70"
                        data-testid={testId}
                      >
                        {inner}
                      </Link>
                    ) : (
                      <a
                        href={link.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="group/link block rounded-lg px-2 py-2 -mx-2 text-sm transition-colors duration-200 hover:bg-sky-50/70"
                        data-testid={testId}
                      >
                        <span className="inline-flex items-start gap-1">
                          <span>{inner}</span>
                          <ArrowUpRight className="w-3 h-3 mt-0.5 shrink-0 text-slate-400" />
                        </span>
                      </a>
                    )}
                  </li>
                );
              })}
            </ul>
          </section>
        ))}
      </div>

      {footnote ? (
        <p className="mt-10 rounded-2xl border border-slate-200 bg-white px-5 py-4 text-xs leading-relaxed text-slate-500">
          {footnote}
        </p>
      ) : null}
    </main>

    <Footer />
  </div>
);

export default SectionDirectory;
